import { useMemo } from 'react';
import { useMediaQuery, Theme, Typography, SxProps } from '@mui/material';
import { get } from 'lodash';
import { Model } from '../types';
import { MoreMenu } from '../components/MoreMenu/MoreMenu.component';
import { DataTableRowProps } from './DataTable.props';
import * as Sx from './DataTable.styles';
import { StyledTableRow, StyledTableCell } from '.';

export const DataTableRow = <T extends Model>({
  columns,
  dataItem,
  activeItem,
  rowIndex,
  isOpen,
  mobileBorder,
  mobileHideMenuItems,
  selected,
  menuItems,
  hasMenuItems,
  disableActiveItem,
  onRowClick,
  rowOnClick,
  isDisabledFunction,
  isSelectedFunction,
  isRowOnError,
  groupKey,
}: DataTableRowProps<T>) => {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  const rowSx: SxProps = useMemo(
    () =>
      Sx.tableBodyRow(
        isRowOnError?.(dataItem),
        !!rowOnClick,
        !!groupKey,
        isOpen,
        mobileBorder
      ),
    [isRowOnError, dataItem, rowOnClick, mobileBorder, isOpen, groupKey]
  );

  return (
    <StyledTableRow
      onClick={() => {
        if (!(isDisabledFunction && isDisabledFunction(dataItem))) {
          onRowClick();
        }
      }}
      className={[
        rowIndex % 2 === 1 ? 'alternate-color' : '',
        activeItem === dataItem && mobileBorder !== 'none' ? 'active-item' : '',
        (!groupKey || isOpen) &&
        selected &&
        isSelectedFunction &&
        selected?.length > 0 &&
        isSelectedFunction(selected, dataItem)
          ? 'selected-item'
          : '',
        isDisabledFunction && isDisabledFunction(dataItem)
          ? 'disabled-item'
          : '',
      ].join(' ')}
      sx={rowSx}
    >
      {columns &&
        columns.map((child, index) => (
          <StyledTableCell
            sx={Sx.tableBodyCell(
              child.props,
              index,
              hasMenuItems,
              activeItem === dataItem,
              disableActiveItem,
              !!groupKey,
              isOpen
            )}
            key={child.props.property.toString()}
          >
            <>
              {index !== 0 && child.props.hasMobileLabel !== false && (
                <Typography
                  variant="caption"
                  sx={Sx.tableBodyCellCaption(child.props)}
                >
                  {child.props.title}
                </Typography>
              )}
              {child.props.activeOutput && activeItem === dataItem
                ? child.props.activeOutput(
                    get(dataItem, child.props.property),
                    dataItem
                  )
                : child.props.output
                ? child.props.output(
                    get(dataItem, child.props.property),
                    dataItem
                  )
                : get(dataItem, child.props.property)}
            </>
          </StyledTableCell>
        ))}
      {hasMenuItems && menuItems && !(mobileHideMenuItems && isMobile) && (
        <StyledTableCell
          className="menu-items"
          sx={Sx.tableBodyCellMenuItem(!!menuItems(dataItem))}
        >
          <MoreMenu>{menuItems(dataItem)}</MoreMenu>
        </StyledTableCell>
      )}
    </StyledTableRow>
  );
};
